import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, pluck, tap, exhaustMap } from 'rxjs/operators';
import { of } from 'rxjs';

import * as fromAccountSettingsActions from '../actions/account-settings.actions';
import { AccountSettingsService } from 'app/settings/account-settings/account-settings.service';
import {
  ChangePasswordParams,
  UserInformation,
} from 'app/settings/account-settings/account-settings.model';

@Injectable()
export class AccountSettingsEffects {
  actions$ = inject(Actions);
  accountSettingsService = inject(AccountSettingsService);

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountSettingsActions.changePassword),
      pluck('passwordData'),
      switchMap((params: ChangePasswordParams) =>
        this.accountSettingsService.changePassword(params).pipe(
          map(() => fromAccountSettingsActions.changePasswordSuccess()),
          catchError(() => of(fromAccountSettingsActions.changePasswordFailed())),
        ),
      ),
    ),
  );

  refreshAccountData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountSettingsActions.refreshUserData),
      exhaustMap(() =>
        this.accountSettingsService.updateUserSettingData().pipe(
          tap((response: UserInformation) => {
            this.accountSettingsService.saveToLocalStore(response.emailAddress);
          }),
          map((response: UserInformation) =>
            fromAccountSettingsActions.refreshUserDataSuccess({ payload: response }),
          ),
          catchError(() => of(fromAccountSettingsActions.refreshUserDataFailed())),
        ),
      ),
    ),
  );

  updateUserInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountSettingsActions.updateUserInfo),
      pluck('userSettingsData'),
      exhaustMap((resp: any) =>
        this.accountSettingsService.updateUserInfo(resp).pipe(
          map(() => fromAccountSettingsActions.updateUserInfoSuccess({ payload: resp })),
          catchError((error: any) => of(fromAccountSettingsActions.updateUserInfoFailed(error))),
        ),
      ),
    ),
  );

  updateUserNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountSettingsActions.updateNotifications),
      pluck('payload'),
      exhaustMap((pushNotificationEnabled: boolean) =>
        this.accountSettingsService.updateUserNotification(pushNotificationEnabled).pipe(
          map(() =>
            fromAccountSettingsActions.updateNotificationsSuccess({
              payload: pushNotificationEnabled,
            }),
          ),
          catchError((error: any) =>
            of(fromAccountSettingsActions.updateNotificationsFailed(error)),
          ),
        ),
      ),
    ),
  );

  initUserDataAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountSettingsActions.initUserDataAfterLogin),
      exhaustMap(() =>
        this.accountSettingsService.updateUserSettingData().pipe(
          tap((response: UserInformation) => {
            this.accountSettingsService.saveToLocalStore(response.emailAddress);
          }),
          map((response: UserInformation) =>
            fromAccountSettingsActions.initUserDataAfterLoginSuccess({ payload: response }),
          ),
          catchError(() => of(fromAccountSettingsActions.initUserDataAfterLoginFail())),
        ),
      ),
    ),
  );
}
